/**
 * Datumsrechnung des Belegungskalenders auf ISO-Tagen (YYYY-MM-DD). Gerechnet wird in UTC, damit
 * ein Tag nie über die Zeitzone des Browsers in den Vortag rutscht; „heute“ kommt aus der Zeitzone
 * des Objekts, nie aus der Browseruhr allein.
 */

export function parseDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function addIsoDays(iso: string, days: number): string {
  const date = parseDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function localIsoDate(timezone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

/** Ein echter Kalendertag im ISO-Format; „2026-02-30“ oder ein leerer Wert sind es nicht. */
export function isCalendarIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = parseDate(value);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

/** Tage von `from` bis `to`, beide eingeschlossen. */
export function daysInRange(from: string, to: string): number {
  const day = 86_400_000;
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / day) + 1;
}

/** Nächte zwischen Anreise und Abreise (Abreisetag ausgeschlossen). */
export function nightsBetween(checkIn: string, checkOut: string): number {
  return Math.max(0, daysInRange(checkIn, checkOut) - 1);
}

export function visibleRange(focus: string, zoom: 7 | 14 | 30 | 60) {
  const from = addIsoDays(focus, -3);
  return { from, to: addIsoDays(from, zoom - 1) };
}

export function isWeekend(iso: string): boolean {
  const dow = parseDate(iso).getUTCDay();
  return dow === 0 || dow === 6;
}

export function monthBreakIndices(dates: string[]): Set<number> {
  const result = new Set<number>();
  for (let i = 1; i < dates.length; i += 1) {
    const d = dates[i];
    if (d && parseDate(d).getUTCDate() === 1) {
      result.add(i);
    }
  }
  return result;
}

const shortDay = new Intl.DateTimeFormat('de-DE', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** „Mo., 19. Okt.“ – für Titel, Ansagen und Auswahllisten statt eines ISO-Tags. */
export function dayLabel(iso: string): string {
  return shortDay.format(parseDate(iso));
}
